import { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation, useSubscription } from "@apollo/client";
import {
    WAVE_DETAIL, TOGGLE_WAVE_FLAG,
    WAVE_FLAG_HISTORY, WAVE_ARM_COUNTS, WAVE_EVENTS_SUB,
} from "../queries/waves";
import WaveMonitorPage from "./WaveMonitorPage";
import SessionsTab from "./SessionsTab";
import ExportTab from "./ExportTab";
import WaveHealthTile from "./WaveHealthTile";
import HarmMetricsTab from "./HarmMetricsTab";
import CloseAttemptsTab from "./CloseAttemptsTab";
import METRIC_TILES from "./metricRegistry";
import { formatDateSGT, formatTimeSGT } from "./dateFormat";

const PHASE_LABELS = {
    0: "Scheduled (not started)",
    1: "Clean baseline",
    2: "Dark pattern active",
    3: "Intervention active",
};

function ArmCountBadges({ waveId }) {
    const { data } = useQuery(WAVE_ARM_COUNTS, { variables: { waveId }, pollInterval: 60000 });
    const counts = data?.waveArmCounts ?? [];
    if (counts.length === 0) return null;
    return (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
            {counts.map(({ arm, count }) => (
                <span key={arm} title={`Number of users assigned to Arm ${arm}`} style={{
                    background: "#f1f5f9", border: "1px solid #cbd5e1",
                    borderRadius: 4, padding: "6px 14px", fontSize: 13,
                }}>
                    Arm {arm} users: <strong>{count}</strong>
                </span>
            ))}
        </div>
    );
}

// Phases advance automatically from start_date — this panel is read-only.
function PhaseStatus({ wave, waveId }) {
    const label = PHASE_LABELS[wave.current_phase] ?? "-";
    const startDate = wave.start_date ? `${formatDateSGT(wave.start_date)}, ${formatTimeSGT(wave.start_date)}` : "-";
    const endDate = wave.end_date ? `${formatDateSGT(wave.end_date)}, ${formatTimeSGT(wave.end_date)}` : "-";
    return (
        <div className="admin-section">
            <h3>Phase and status</h3>
            <p>
                Status: <strong>{wave.status}</strong>
                {" "}&middot;{" "}Phase: <strong>{wave.current_phase}</strong> ({label})
            </p>
            <p style={{ color: "#666", fontSize: 13 }}>
                Phases advance automatically based on the wave start date.
                Runs from {startDate} to {endDate}.
            </p>
            <ArmCountBadges waveId={waveId} />
        </div>
    );
}

function FlagToggles({ wave, waveId, currentFlags, refetch }) {
    const [busyKey, setBusyKey] = useState(null);
    const [errMsg, setErrMsg] = useState(null);
    const [toggle] = useMutation(TOGGLE_WAVE_FLAG);
    const isRunning = wave.status === "running";

    // Some dark patterns (obstruction) are fully phase/arm-driven with no
    // togglable flags at all — don't render an empty "Flags" section for them.
    if (currentFlags.length === 0) return null;

    // Set a flag, handling the "wave is running" confirmation guard. The backend
    // rejects the first call without confirmed:true so the admin sees a warning.
    const setFlag = async (flagKey, nextValue) => {
        setBusyKey(flagKey);
        setErrMsg(null);
        try {
            await toggle({ variables: { waveId, flagKey, value: nextValue } });
            await refetch();
        } catch (err) {
            if (err.message.includes("currently running")) {
                const ok = window.confirm(
                    `Wave ${waveId} is live. Changing "${flagKey}" affects participants ` +
                    `mid-session. Continue?`
                );
                if (ok) {
                    try {
                        await toggle({ variables: { waveId, flagKey, value: nextValue, confirmed: true } });
                        await refetch();
                    } catch (err2) {
                        setErrMsg(err2.message);
                    }
                }
            } else {
                setErrMsg(err.message);
            }
        } finally {
            setBusyKey(null);
        }
    };

    return (
        <div className="admin-section">
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
                <h3 style={{ margin: 0 }}>Flags</h3>
                {isRunning && (
                    <span style={{
                        background: "#fef3c7", border: "1px solid #fbbf24", color: "#92400e",
                        borderRadius: 4, padding: "4px 12px", fontSize: 12, fontWeight: 600,
                    }}>
                        Wave is live
                    </span>
                )}
            </div>
            {errMsg && <p style={{ color: "crimson", fontSize: 13 }}>{errMsg}</p>}
            <table className="admin-table">
                <thead><tr><th>Flag</th><th>Enabled</th><th>Changed Date</th><th>Changed Time</th><th></th></tr></thead>
                <tbody>
                    {currentFlags.map(f => {
                        const on = f.new_value === "true";
                        return (
                            <tr key={f.flag_key}>
                                <td>{f.flag_key}</td>
                                <td style={{ color: on ? "#16a34a" : "#888", fontWeight: 600 }}>{on ? "true" : "false"}</td>
                                <td>{f.changed_at ? formatDateSGT(f.changed_at) : "-"}</td>
                                <td>{f.changed_at ? formatTimeSGT(f.changed_at) : "-"}</td>
                                <td>
                                    <button className="admin-btn" disabled={busyKey === f.flag_key}
                                        onClick={() => setFlag(f.flag_key, on ? "false" : "true")}>
                                        {busyKey === f.flag_key ? "Working" : on ? "Disable" : "Enable"}
                                    </button>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

function AuditLog({ waveId }) {
    const { loading, data, error } = useQuery(WAVE_FLAG_HISTORY, { variables: { waveId, limit: 50, offset: 0 } });
    return (
        <div className="admin-section">
            <h3>Audit log</h3>
            {loading && <p>Loading…</p>}
            {error && <p style={{ color: "crimson" }}>{error.message}</p>}
            {data && (
                <table className="admin-table">
                    <thead><tr><th>Flag</th><th>Old</th><th>New</th><th>Changed by</th><th>Date</th><th>Time</th></tr></thead>
                    <tbody>
                        {data.waveFlagHistory.length === 0 && (
                            <tr><td colSpan={6} style={{ color: "#888", textAlign: "center" }}>No changes yet.</td></tr>
                        )}
                        {data.waveFlagHistory.map(f => (
                            <tr key={f.id}>
                                <td>{f.flag_key}</td>
                                <td>{f.old_value || "-"}</td>
                                <td>{f.new_value}</td>
                                <td>{f.changed_by}</td>
                                <td>{f.changed_at ? formatDateSGT(f.changed_at) : "-"}</td>
                                <td>{f.changed_at ? formatTimeSGT(f.changed_at) : "-"}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}

const TABS = [
    { id: "monitor", label: "Live monitor" },
    { id: "sessions", label: "Sessions" },
    { id: "harm", label: "Harm metrics" },
    // "attempts" is inserted here for obstruction waves only (see buildTabs)
    { id: "audit", label: "Audit log" },
    { id: "export", label: "Export" },
    { id: "health", label: "Health" },
];

// Close Attempts is Wave E (obstruction) research data — only show it there.
const buildTabs = (darkPattern) => {
    if (darkPattern !== "obstruction") return TABS;
    const i = TABS.findIndex(t => t.id === "harm");
    return [...TABS.slice(0, i + 1), { id: "attempts", label: "Close attempts" }, ...TABS.slice(i + 1)];
};

export default function WaveDetailPage() {
    const { waveId } = useParams();
    const [tab, setTab] = useState("monitor");
    // Bumped on each PING so the Sessions tab knows to reload (guide §7).
    const [sessionRefreshKey, setSessionRefreshKey] = useState(0);
    const { loading, error, data, refetch } = useQuery(WAVE_DETAIL, { variables: { waveId } });

    // Live event stream (guide §7): keep the flag panel and session list current
    // without polling. FLAG_CHANGED refetches the flags; PING reloads sessions.
    useSubscription(WAVE_EVENTS_SUB, {
        variables: { waveId },
        onSubscriptionData: ({ subscriptionData }) => {
            const evt = subscriptionData?.data?.waveEvents;
            if (!evt) return;
            if (evt.type === "FLAG_CHANGED") refetch();
            if (evt.type === "PING") setSessionRefreshKey((k) => k + 1);
        },
    });

    if (loading) return <p>Loading…</p>;
    if (error) return <p style={{ color: "crimson" }}>{error.message}</p>;
    if (!data?.waveDetail) return <p>Not found.</p>;

    const { wave, currentFlags } = data.waveDetail;
    const metricTiles = METRIC_TILES[wave.dark_pattern] || [];
    const tabs = buildTabs(wave.dark_pattern);

    return (
        <div>
            <h1>Wave #{wave.wave_id}: {wave.name || wave.dark_pattern}</h1>
            <p style={{ color: "#666", fontSize: 13, marginTop: 6, marginBottom: 24 }}>
                {wave.dark_pattern} &middot; Contest #{wave.contest_id} &middot; Session window {wave.session_window_seconds}s
                {" "}&middot; Created {wave.created_at ? `${formatDateSGT(wave.created_at)}, ${formatTimeSGT(wave.created_at)}` : "-"}
            </p>

            <PhaseStatus wave={wave} waveId={waveId} />
            <FlagToggles wave={wave} waveId={waveId} currentFlags={currentFlags} refetch={refetch} />

            {/* Dark-pattern metric tiles (populated by M2/M3/M4) */}
            {metricTiles.map((Tile, i) => (
                <Tile key={i} waveId={waveId} wave={wave} />
            ))}

            <div className="admin-tabs">
                {tabs.map(t => (
                    <div key={t.id}
                        className={`admin-tab ${tab === t.id ? "admin-tab-active" : ""}`}
                        onClick={() => setTab(t.id)}>
                        {t.label}
                    </div>
                ))}
            </div>

            {tab === "monitor" && <WaveMonitorPage waveId={waveId} />}
            {tab === "sessions" && <SessionsTab waveId={waveId} refreshKey={sessionRefreshKey} />}
            {tab === "harm" && <HarmMetricsTab waveId={waveId} />}
            {tab === "attempts" && <CloseAttemptsTab waveId={waveId} />}
            {tab === "audit" && <AuditLog waveId={waveId} />}
            {tab === "export" && <ExportTab waveId={waveId} />}
            {tab === "health" && <WaveHealthTile waveId={waveId} wave={wave} currentFlags={currentFlags} />}
        </div>
    );
}
